import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ApiError } from '@/api'
import { useAuth } from '@/contexts/AuthContext'
import { routes } from '@/routes'
import { AuthCard } from '@/components/auth/AuthCard'
import { RedirectingNotice } from '@/components/auth/RedirectingNotice'
import { Turnstile } from '@/components/auth/Turnstile'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { TextField } from '@/components/ui/Field'

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [turnstileToken, setTurnstileToken] = useState<string>()
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [status, setStatus] = useState<'idle' | 'submitting' | 'redirecting'>('idle')

  /** Volta para onde o usuário estava antes de ser mandado ao login. */
  const from = (location.state as { from?: string } | null)?.from ?? routes.home

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setFieldErrors({})
    setStatus('submitting')

    try {
      const user = await login({ email, password, turnstileToken })
      setStatus('redirecting')
      // Admin cai direto na área de produtos; usuário comum volta para a origem.
      navigate(user.role === 'admin' ? routes.admin : from, { replace: true })
    } catch (err) {
      setStatus('idle')
      if (err instanceof ApiError) {
        setError(err.message)
        setFieldErrors(err.fieldErrors ?? {})
      } else {
        setError('Não foi possível entrar. Tente novamente.')
      }
    }
  }

  return (
    <AuthCard title="Faça login ou crie uma conta">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {error && <Alert tone="danger">{error}</Alert>}

        <TextField
          label="E-mail"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={fieldErrors.email}
        />

        <TextField
          label="Senha"
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={fieldErrors.password}
          labelAside={
            <Link
              to={routes.forgotPassword}
              className="text-[13px] text-ink-soft transition-colors hover:text-brand hover:underline"
            >
              Esqueceu a senha?
            </Link>
          }
        />

        <Turnstile onVerify={setTurnstileToken} onExpire={() => setTurnstileToken(undefined)} />

        {status === 'redirecting' ? (
          <RedirectingNotice />
        ) : (
          <Button type="submit" size="lg" block loading={status === 'submitting'}>
            Entrar
          </Button>
        )}

        <Link
          to={routes.register}
          className="py-1 text-center text-[15px] font-bold text-brand transition-colors hover:text-brand-dark"
        >
          Criar conta
        </Link>
      </form>
    </AuthCard>
  )
}
